import { match } from 'ts-pattern';
import { memoEval } from '@salsita-ai/platform-project-sdk/domain';
import { implementCustomExpressions } from '../__generated/implementation.js';
const getMaterialProperties = (collectionMap, baseProperties, collectionKey, materialKey)=>{
    const collection = collectionMap[collectionKey];
    const design = collection?.designs?.[materialKey] ?? {
        type: 'color',
        name: 'default',
        key: 'default',
        swatch: '#ffffff',
        material: '#ffffff'
    };
    return match(design).with({
        type: 'color'
    }, (colorDesign)=>({
            ...baseProperties.color,
            textureUrl: baseProperties.texture.textureUrl,
            color: colorDesign.material
        })).with({
        type: 'texture'
    }, (textureDesign)=>({
            ...baseProperties.texture,
            textureUrl: textureDesign.texture,
            repeatX: textureDesign.repeatX,
            repeatY: textureDesign.repeatY,
            offsetX: textureDesign.offsetX,
            offsetY: textureDesign.offsetY
        })).exhaustive();
};
export const renderingExpressions = implementCustomExpressions({
    Shorts: {
        $customProductDescription: (s)=>{
            const htmlDescription = `
        <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          <h3 style="margin-bottom: 10px; color: #000;">Your Custom Shorts Configuration</h3>
            
          <div style="margin-bottom: 15px;">
            <h4 style="margin-bottom: 5px; color: #555;">Shorts Details</h4>
            <ul style="list-style-type: none; padding-left: 0;">
              <li><strong>Size:</strong> ${s.size.toUpperCase()}</li>
              <li><strong>Inseam:</strong> ${s.inseam > 0 ? '+' : ''}${s.inseam}"</li>
              <li><strong>Fabric:</strong> ${s.fabric}</li>
              <li><strong>Liner:</strong> ${match(s.liner).with('none', ()=>'No liner').with('mesh', ()=>'Leisure liner').with('compression', ()=>'Compression liner').exhaustive()}</li>
            </ul>
          </div>
              
          <div style="margin-bottom: 15px;">
            <h4 style="margin-bottom: 5px; color: #555;">Design</h4>
            <ul style="list-style-type: none; padding-left: 0;">
              <li><strong>Left Leg:</strong> ${s.$leftLeg.$selectedCollection.name} - ${s.$leftLeg.$selectedDesign.name}</li>
              <li><strong>Right Leg:</strong> ${s.$rightLeg.$selectedCollection.name} - ${s.$rightLeg.$selectedDesign.name}</li>
              <li><strong>Left Thigh Band:</strong> ${s.$leftThighBand.$selectedCollection.name} - ${s.$leftThighBand.$selectedDesign.name}</li>
              <li><strong>Right Thigh Band:</strong> ${s.$rightThighBand.$selectedCollection.name} - ${s.$rightThighBand.$selectedDesign.name}</li>
              <li><strong>Waistband:</strong> ${s.$waistband.$selectedCollection.name} - ${s.$waistband.$selectedDesign.name}</li>
              <li><strong>Drawstring:</strong> ${s.$drawstring.$selectedColor.name}</li>
              <li><strong>Stitches:</strong> ${s.$collectionMap[s.collection]?.designs?.[s.design]?.stitchesColorName ?? 'No Color Selected'}</li>
            </ul>
          </div>
        </div>
      `.trim();
            return htmlDescription;
        }
    },
    SwimShorts: {
        $customProductTitle: (self)=>`Sunlife Custom Swim Shorts ${self.size.toUpperCase()} ${self.inseam > 0 ? '+' : ''}${self.inseam}"`,
        $isCompressionLiner: (self)=>self.liner === 'compression',
        $isMeshLiner: (self)=>self.liner === 'mesh',
        $leftLegMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$leftLeg.collection,
                self.$leftLeg.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'LegLeftMaterial',
                    'ThighBandLeftMaterial'
                ]
            })),
        $rightLegMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$rightLeg.collection,
                self.$rightLeg.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'LegRightMaterial',
                    'PocketMaterial',
                    'ThighBandRightMaterial'
                ]
            })),
        $leftThighBandMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$leftThighBand.collection,
                self.$leftThighBand.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'ThighBandLeftMaterial'
                ]
            })),
        $rightThighBandMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$rightThighBand.collection,
                self.$rightThighBand.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'ThighBandRightMaterial'
                ]
            })),
        $waistbandMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$waistband.collection,
                self.$waistband.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'WaistbandMaterial'
                ]
            })),
        $drawstringMaterialColor: memoEval((self)=>[
                self.$drawstring.$availableColors,
                self.$drawstring.color
            ], (availableColors, colorKey)=>{
            const color = availableColors.find((c)=>c.key === colorKey);
            return color?.material ?? '#ffffff';
        }),
        $stitchesMaterialColor: memoEval((self)=>[
                self.$collectionMap,
                self.collection,
                self.design
            ], (collectionMap, collectionKey, designKey)=>{
            return collectionMap[collectionKey]?.designs?.[designKey]?.stitches ?? '#ffffff';
        })
    },
    BoardShorts: {
        $customProductTitle: (self)=>`Sunlife Custom Board Shorts ${self.size.toUpperCase()} ${self.inseam > 0 ? '+' : ''}${self.inseam}"`,
        $isCompressionLiner: (self)=>self.liner === 'compression',
        $isMeshLiner: (self)=>self.liner === 'mesh',
        $leftLegMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$leftLeg.collection,
                self.$leftLeg.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'LegLeftMaterial',
                    'ThighBandLeftMaterial'
                ]
            })),
        $rightLegMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$rightLeg.collection,
                self.$rightLeg.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'LegRightMaterial',
                    'PocketMaterial',
                    'ThighBandRightMaterial'
                ]
            })),
        $leftThighBandMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$leftThighBand.collection,
                self.$leftThighBand.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'ThighBandLeftMaterial'
                ]
            })),
        $rightThighBandMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$rightThighBand.collection,
                self.$rightThighBand.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'ThighBandRightMaterial'
                ]
            })),
        $waistbandMaterial: memoEval((self)=>[
                self.$collectionMap,
                self.$materialBaseProperties,
                self.$waistband.collection,
                self.$waistband.design
            ], (collectionMap, baseProperties, collectionKey, designKey)=>({
                ...getMaterialProperties(collectionMap, baseProperties, collectionKey, designKey),
                materialsToOverride: [
                    'WaistbandMaterial'
                ]
            })),
        $drawstringMaterialColor: memoEval((self)=>[
                self.$drawstring.$availableColors,
                self.$drawstring.color
            ], (availableColors, colorKey)=>{
            const color = availableColors.find((c)=>c.key === colorKey);
            return color?.material ?? '#ffffff';
        }),
        $stitchesMaterialColor: memoEval((self)=>[
                self.$collectionMap,
                self.collection,
                self.design
            ], (collectionMap, collectionKey, designKey)=>{
            return collectionMap[collectionKey]?.designs?.[designKey]?.stitches ?? '#ffffff';
        })
    }
});

//# sourceMappingURL=rendering.js.map